import { useAppStore } from "@/stores";
import { getAll, getDeals } from "@/lib/supabase";
import useSWR from "swr";
import {
  addMonths,
  format,
  isAfter,
  parse,
} from "date-fns";
import {
  evaluateRiskIndicators,
  getWeightedDealRiskScoreInCategory,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import { getStageProbability } from "../lib/pipelines";
import { Database } from "@revelate/types";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  TooltipProps as RechartsTooltipProps,
  Legend,
  ComposedChart,
  Line,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getAPI } from "@/lib/api";
import DatePicker from "@/currencies/components/date-picker";
import { useState } from "react";
import { Loader } from "@/components/Loader";
import { LoadingError } from "@/components/LoadingError";
import { getHistoricalPropertyAtSnapshotDate } from "../lib/history";
import { buildRiskHistoryDealSnapshot } from "../lib/risk-history";
// import { Switch } from "@/components/ui/switch";


export function RevenueIntelligenceHistoryPage() {
  const [snapshotDate, setSnapshotDate] = useState<Date | undefined>(new Date("2026-01-01"));
  const { currentUser, companyCurrency } = useAppStore((state) => state);

  const {
    data: dealsForHistoricalComparison,
    error: dealsForHistoricalComparisonError,
    isLoading: isLoadingDealsForHistoricalComparison,
  } = useSWR(
    [
      'query:dealsForHistoricalComparison',
      currentUser ? currentUser?.company_id.toString() : null,
      snapshotDate,
    ],
    ([_, companyId, snapshotDate]) => (companyId === null || snapshotDate === undefined)
      ? []
      : getDeals(companyId, {
        dateRange: {
          from: snapshotDate.toISOString(),
          to: addMonths(snapshotDate, 3).toISOString(),
        },
        nonZeroValue: true,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: currencies,
    // error: currenciesError,
    // isLoading: isLoadingCurrencies,
  } = useSWR(
    [
      'query:currencies',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAll("currencies", companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: pipelines,
    error: pipelinesError,
    isLoading: isLoadingPipelines,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAPI(`providers/hubspot/pipelines/${companyId}`),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  if (
    isLoadingDealsForHistoricalComparison ||
    isLoadingPipelines
  ) return <Loader />;

  if (
    dealsForHistoricalComparisonError ||
    pipelinesError
  ) return <LoadingError />;

  const hubspotDealsForHistoricalComparison = prepareDealsForRiskAssessment(
    dealsForHistoricalComparison,
    pipelines,
    // @ts-ignore
    currencies,
    currentUser?.company,
    undefined,
  );

  const {
    historicalComparisonChartData: chartData,
  } = evaluateRiskHistoricalForecast(
    hubspotDealsForHistoricalComparison,
    snapshotDate,
    pipelines?.results ?? [],
    currentUser?.company_id ?? null,
  );


  return (
    <div className="flex flex-col gap-y-10">
      <h1>Revenue Intelligence Historical Comparison</h1>
      <div className="flex flex-row justify-start items-center gap-x-3">
        {/* Date picker */}
        <div className="flex flex-row gap-x-3 items-center">
          <p className="text-gray-500 text-sm">
            Snapshot at
          </p>
          <DatePicker
            date={snapshotDate}
            setDate={setSnapshotDate}
            placeholder="No date selected"
          />
        </div>
      </div>
      <HistoricalComparisonChart
        data={chartData}
        companyCurrency={companyCurrency}
      />
    </div>
  );
}

function evaluateRiskHistoricalForecast(
  hubspotDeals: any[],
  snapshotDate: Date | undefined,
  pipelines: any[],
  companyId: number | null,
) {
  if (snapshotDate === undefined) {
    return { historicalComparisonChartData: [] };
  }

  const dealsCreatedBeforeOrAtSnapshotTimeIds = hubspotDeals
    .filter(d => !isAfter(d.provider_data?.properties?.createdate ?? d.provider_data?.properties?.hs_createdate ?? "", snapshotDate))
    .map(d => d.id)

  const openDealsAtSnapshotTime = hubspotDeals
    .filter(d => {
      const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", snapshotDate);
      const probabilityAtSnapshotTime = getStageProbability(pipelines, d.pipeline, dealstageAtSnapshotTime);
      const isOpen = probabilityAtSnapshotTime < 1;
      return isOpen;
    })
    .filter(d => dealsCreatedBeforeOrAtSnapshotTimeIds.includes(d.id))

  const wonDealsAtSnapshotTime = hubspotDeals
    .filter(d => d.is_won)
    .filter(d => dealsCreatedBeforeOrAtSnapshotTimeIds.includes(d.id))

  const openDealsAtSnapshotTimeReadyForRiskAssessment = openDealsAtSnapshotTime.map(d => {
    const snapshotDeal = buildRiskHistoryDealSnapshot(d, snapshotDate);
    // NOTE: Setting to full 10 to avoid taking this factor into account
    const dealValueAsPctOfTotalValue = 10;

    const dealstageAtSnapshotTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", snapshotDate);
    const probabilityAtSnapshotTime = getStageProbability(pipelines, d.pipeline, dealstageAtSnapshotTime, true);

    const closeDate = d.closed_at ? new Date(d.closed_at) : undefined;
    const dealstageAtCloseTime = getHistoricalPropertyAtSnapshotDate(d, "dealstage", closeDate);
    const probabilityAtCloseTime = getStageProbability(pipelines, d.pipeline, dealstageAtCloseTime ?? null, true);

    return {
      ...snapshotDeal,
      percentage_of_total_value: dealValueAsPctOfTotalValue,
      deal_scale: dealValueAsPctOfTotalValue,
      stage_scale_at_snapshot_time: probabilityAtSnapshotTime,
      stage_scale_at_close_time: probabilityAtCloseTime,
    };
  })

  const openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated = evaluateRiskIndicators(
    openDealsAtSnapshotTimeReadyForRiskAssessment,
    companyId,
    pipelines,
    undefined,
    snapshotDate,
  );

  const openDealsAtSnapshotTimeWithRiskScores = openDealsAtSnapshotTimeWithRiskIndicatorsEvaluated
    .map((d: any) => ({
      ...d,
      risk_score: getWeightedDealRiskScoreInCategory(d, "forecast"),
    }))
    .toSorted((a, b) => b.risk_score - a.risk_score);

  const historicalComparisonChartData =
    formatDealsForHistoricalComparisonChart(
      openDealsAtSnapshotTimeWithRiskScores,
      wonDealsAtSnapshotTime,
    );

  return {
    historicalComparisonChartData,
  };
}

type ForecastTrendChartRow = {
  month: string;
  won: number;
  crm_forecast: number;
  risk_adjusted: number;
};

function formatDealsForHistoricalComparisonChart(
  openDealsAtSnapshotTimeWithRisk: any[],
  wonDealsAtSnapshotTime: any[],
): ForecastTrendChartRow[] {
  const grouped: Record<string, ForecastTrendChartRow> = {};

  for (const deal of wonDealsAtSnapshotTime) {
    const dealValue = Math.round(deal.value);
    const date = new Date(deal.closed_at);

    // Bucket by month (YYYY-MM)
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    if (!grouped[month]) {
      grouped[month] = {
        month,
        risk_adjusted: 0,
        crm_forecast: 0,
        won: 0,
      };
    }

    grouped[month].won += dealValue;
  }

  for (const deal of openDealsAtSnapshotTimeWithRisk) {
    const date = new Date(deal.closed_at);

    // Bucket by month (YYYY-MM)
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    if (!grouped[month]) {
      grouped[month] = {
        month,
        won: 0,
        crm_forecast: 0,
        risk_adjusted: 0,
      };
    }

    const hasRisks = Object.keys(deal.risks).length > 0;

    const closeProbability = deal.stage_scale_at_close_time ?? 1;
    const scaledDealValue = Math.round(deal.value * closeProbability);

    grouped[month].risk_adjusted += scaledDealValue;
    const crmForecast = scaledDealValue;

    grouped[month].crm_forecast += crmForecast;

    if (hasRisks) {
      const proportionalValueAtRisk = Math.round(scaledDealValue * deal.risk_score / 100);
      grouped[month].risk_adjusted -= proportionalValueAtRisk;
    }
  }

  return Object
    .values(grouped)
    .sort((a, b) => a.month.localeCompare(b.month));
}

interface HistoricalComparisonChartProps {
  data: any[],
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
}

function HistoricalComparisonChart({ data, companyCurrency }: HistoricalComparisonChartProps) {
  const formatAxisCurrency = (value: number) =>
    formatAxisTickValue(value, companyCurrency);

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%">
        <ComposedChart data={data}>
          <XAxis
            dataKey="month"
            tickFormatter={(value) => {
              const date = parse(value, "yyyy-MM", new Date());
              return format(date, "MMM");
            }}
            fontSize={12}
          />

          <YAxis
            fontSize={12}
            tickFormatter={(value) => formatAxisCurrency(value)}
            domain={[
              (dataMin: number) => dataMin * 0.95,
              (dataMax: number) => dataMax * 1.05,
            ]}
          />

          <RechartsTooltip
            formatter={(value: number) => value.toLocaleString()}
            content={<CustomTooltipForecastChart />}
          />

          <Legend content={<CustomLegendHistoricalComparisonChart />} />

          <Line
            type="monotone"
            dataKey="won"
            stroke="#3A3361"
            name="Won"
            strokeWidth={2}
            fillOpacity={0.3}
          />

          <Line
            type="monotone"
            dataKey="risk_adjusted"
            stroke="#9F86C0"
            fill="white"
            name="Forecast"
            strokeWidth={2}
            fillOpacity={1}
          />

          <Line
            type="monotone"
            dataKey="crm_forecast"
            stroke="#6B7280" // tailwind bg-gray-600
            name="CRM Forecast"
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div >
  );
}

function CustomLegendHistoricalComparisonChart() {
  return (
    <div className="flex flex-row justify-center items-center gap-x-5 mt-1">

      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-3 h-3 rounded-full bg-[#3A3361]" />
        <p className="text-gray-500 text-sm">
          Won
        </p>
      </div>

      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-3 h-3 rounded-full bg-gray-400" />
        <p className="text-gray-500 text-sm font-normal">
          CRM Forecast
        </p>
      </div>

      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-3 h-3 rounded-full bg-[#9F86C0]" />
        <p className="text-gray-500 text-sm font-normal">
          Risk-adjusted Forecast
        </p>
      </div>
    </div>
  );
};

function CustomTooltipForecastChart({ active, payload, label }: RechartsTooltipProps<number, string>) {
  const { companyCurrency } = useAppStore((state) => state);
  const currencyCode = companyCurrency?.code ?? "SEK";

  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const month = row?.month;

  const riskAdjusted = payload.find(p => p.dataKey === "risk_adjusted")?.value ?? 0;
  const crmForecast = payload.find(p => p.dataKey === "crm_forecast")?.value ?? 0;
  const won = payload.find(p => p.dataKey === "won")?.value ?? 0;

  const currencyFormatter = new Intl.NumberFormat("sv-SE")

  return (
    <Card className="w-72 p-0" key={label}>
      <CardHeader className="p-4">
        <CardTitle className="text-xs font-bold font-display pb-2">
          {month}
        </CardTitle>

        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span>Won</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-[#3A3361]" />
              <span>{currencyFormatter.format(Number(won))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span>CRM Forecast</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-gray-400" />
              <span>{currencyFormatter.format(Number(crmForecast))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span>Risk-adjusted Forecast</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-[#9F86C0]" />
              <span>{currencyFormatter.format(Number(riskAdjusted))} {currencyCode}</span>
            </span>
          </div>

        </div>
      </CardHeader>
    </Card>
  );
};

function formatAxisTickValue(
  value: number,
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null,
  decimalPlaces = 0,
) {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);

  let formattedValue =
    absoluteValue >= 1_000_000
      ? `${(absoluteValue / 1_000_000).toFixed(decimalPlaces)}M`
      : absoluteValue >= 1_000
        ? `${(absoluteValue / 1_000).toFixed(decimalPlaces)}K`
        : absoluteValue.toFixed(decimalPlaces);

  if (isNegative) {
    formattedValue = `-${formattedValue}`
  }

  return `${formattedValue} ${companyCurrency?.code ?? "SEK"}`;
}
